<template>
  <div style="max-width: 1400px; margin: auto" class="pa-3">
    <!-- Header -->
    <v-toolbar dark flat color="transparent" class="px-0 mb-3">
      <v-toolbar-title>
        <v-icon left>mdi-account-tie</v-icon>
        Bewerbungen
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-chip v-if="newCount > 0" small color="red" dark class="mr-2">
        <v-icon left small>mdi-bell-ring</v-icon>
        {{ newCount }} neu
      </v-chip>
      <v-btn icon @click="loadApplications" :loading="loading" title="Aktualisieren">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </v-toolbar>

    <!-- Filter -->
    <v-card dark class="mb-3 pa-3" color="rgba(255,255,255,0.05)" flat>
      <v-row dense>
        <v-col v-if="canManage" cols="12" sm="6" md="4">
          <v-select
            v-model="filterStudio"
            :items="studioFilterOptions"
            item-text="name"
            item-value="_id"
            label="Studio"
            outlined
            dense
            dark
            hide-details
            @change="loadApplications" />
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <v-select v-model="filterStatus" :items="statusFilterOptions" label="Status" outlined dense dark hide-details @change="loadApplications" />
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <v-text-field
            v-model="searchQuery"
            label="Suche (Name, E-Mail, Stelle)"
            outlined
            dense
            dark
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify" />
        </v-col>
      </v-row>
    </v-card>

    <!-- Liste -->
    <v-card dark color="rgba(255,255,255,0.03)" flat>
      <v-data-table
        dark
        :headers="tableHeaders"
        :items="filteredApplications"
        :loading="loading"
        :items-per-page="25"
        class="transparent"
        no-data-text="Keine Bewerbungen vorhanden"
        @click:row="openDetailDialog">
        <template v-slot:item.createdAt="{ item }">
          {{ $moment(item.createdAt).format("DD.MM.YYYY HH:mm") }}
        </template>
        <template v-slot:item.assignedUsers="{ item }">
          <div>
            <strong>{{ item.assignedUsers.map((user) => `${user.vorname} ${user.nachname}`).join(", ") }}</strong>
          </div>
        </template>
        <template v-slot:item.applicant="{ item }">
          <div>
            <strong>{{ item.firstName }} {{ item.lastName }}</strong>
            <div class="caption grey--text">{{ item.email }}</div>
          </div>
        </template>
        <template v-slot:item.jobOffer="{ item }">
          {{ item.jobOffer ? item.jobOffer.title || (item.jobOffer.type && item.jobOffer.type.title) || "-" : "-" }}
        </template>
        <template v-slot:item.studio="{ item }">
          {{ item.studio ? item.studio.name : "-" }}
        </template>
        <template v-slot:item.attachments="{ item }">
          <v-chip x-small color="blue-grey" dark v-if="item.attachments && item.attachments.length > 0">
            <v-icon left x-small>mdi-paperclip</v-icon>
            {{ item.attachments.length }}
          </v-chip>
          <span v-else class="grey--text caption">—</span>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip x-small :color="statusColor(item.status)" dark>
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn icon small @click.stop="openDetailDialog(item)">
            <v-icon small>mdi-eye</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- DETAIL DIALOG -->
    <v-dialog :fullscreen="$vuetify.breakpoint.smAndDown" v-model="detailDialog" max-width="1000" scrollable>
      <v-card dark v-if="currentApplication">
        <v-card-title>
          <v-icon left>mdi-account-tie</v-icon>
          {{ currentApplication.firstName }} {{ currentApplication.lastName }}
          <v-spacer></v-spacer>
          <v-chip small :color="statusColor(currentApplication.status)" dark class="mr-2">
            {{ statusLabel(currentApplication.status) }}
          </v-chip>
          <v-btn icon v-if="canEdit(currentApplication)" @click="openEditDialog" title="Bearbeiten">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon @click="detailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text style="max-height: 75vh">
          <v-row class="mt-2">
            <v-col cols="12" md="6">
              <h4 class="mb-2">Persönliche Daten</h4>

              <div class="d-flex mb-1" style="font-size: 13px">
                <span class="grey--text" style="min-width: 140px">Name:</span>
                <span>{{ currentApplication.firstName + " " + currentApplication.lastName || "-" }}</span>
              </div>

              <div class="d-flex mb-1" style="font-size: 13px">
                <span class="grey--text" style="min-width: 140px">E-Mail:</span>
                <a :href="'mailto:' + currentApplication.email" class="green--text text--lighten-1">
                  {{ currentApplication.email || "-" }}
                </a>
              </div>

              <div class="d-flex mb-1" style="font-size: 13px">
                <span class="grey--text" style="min-width: 140px">Telefon:</span>
                <a :href="'tel:' + currentApplication.phone" class="green--text text--lighten-1">
                  {{ currentApplication.phone || "-" }}
                </a>
              </div>

              <div class="d-flex mb-1" style="font-size: 13px">
                <span class="grey--text" style="min-width: 140px">Geburtsdatum:</span>
                <span>{{ formatDate(currentApplication.birthDate) || "-" }}</span>
              </div>

              <div class="d-flex mb-1" style="font-size: 13px">
                <span class="grey--text" style="min-width: 140px">Adresse:</span>
                <span>{{ formatAddress(currentApplication.address) || "-" }}</span>
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <h4 class="mb-2">Bewerbung</h4>

              <div class="d-flex mb-1" style="font-size: 13px">
                <span class="grey--text" style="min-width: 140px">Stelle:</span>
                <span>{{
                  currentApplication.jobOffer
                    ? currentApplication.jobOffer.title || (currentApplication.jobOffer.type && currentApplication.jobOffer.type.title) || "-"
                    : "-"
                }}</span>
              </div>

              <div class="d-flex mb-1" style="font-size: 13px">
                <span class="grey--text" style="min-width: 140px">Studio:</span>
                <span>{{ (currentApplication.studio && currentApplication.studio.name) || "-" }}</span>
              </div>

              <div class="d-flex mb-1" style="font-size: 13px">
                <span class="grey--text" style="min-width: 140px">Pensum:</span>
                <span>{{ currentApplication.desiredWorkload || "-" }}</span>
              </div>

              <div class="d-flex mb-1" style="font-size: 13px">
                <span class="grey--text" style="min-width: 140px">Frühster Eintritt:</span>
                <span>{{ formatDate(currentApplication.earliestStartDate) || "-" }}</span>
              </div>

              <div class="d-flex mb-1" style="font-size: 13px">
                <span class="grey--text" style="min-width: 140px">Eingegangen:</span>
                <span>{{ formatDateTime(currentApplication.createdAt) || "-" }}</span>
              </div>

              <div class="d-flex mb-1" style="font-size: 13px">
                <span class="grey--text" style="min-width: 140px">Mail versendet:</span>
                <span>{{ currentApplication.initialMailSent ? "JA" : "NEIN" }}</span>
              </div>
            </v-col>
          </v-row>

          <!-- Anschreiben -->
          <div v-if="currentApplication.coverLetter" class="mt-4">
            <h4 class="mb-2"><v-icon left small>mdi-text-box-outline</v-icon>Anschreiben</h4>
            <v-card flat color="rgba(255,255,255,0.05)" class="pa-4" dark>
              <div style="white-space: pre-wrap; line-height: 1.6">
                {{ currentApplication.coverLetter }}
              </div>
            </v-card>
          </div>

          <!-- Anhänge -->
          <div v-if="currentApplication.attachments && currentApplication.attachments.length > 0" class="mt-4">
            <h4 class="mb-2">
              <v-icon left small>mdi-paperclip</v-icon>
              Anhänge ({{ currentApplication.attachments.length }})
            </h4>
            <attachment-list :application-id="currentApplication._id" :attachments="currentApplication.attachments" />
          </div>

          <v-divider class="my-4"></v-divider>

          <!-- Zuständigkeit -->
          <h4 class="mb-2">
            <v-icon left small>mdi-account-multiple</v-icon>
            Zuständig
          </h4>
          <div class="mb-3">
            <v-chip v-for="user in currentApplication.assignedUsers" :key="user._id" small color="primary" dark class="mr-2 mb-1">
              <v-icon left x-small>mdi-account</v-icon>
              {{ user.vorname }} {{ user.nachname }}
            </v-chip>
            <span v-if="!currentApplication.assignedUsers || currentApplication.assignedUsers.length === 0" class="grey--text caption">
              Keine User zugewiesen
            </span>
          </div>
          <v-btn small color="orange" outlined @click="openForwardDialog"> <v-icon left small>mdi-share</v-icon>Weiterleiten </v-btn>

          <v-divider class="my-4"></v-divider>

          <!-- Status verwalten -->
          <div class="mb-4">
            <h4 class="mb-2">Status</h4>
            <v-btn-toggle v-model="currentApplication.status" mandatory dense @change="changeStatus">
              <v-btn small value="new">Neu</v-btn>
              <v-btn small value="in_progress">In Bearbeitung</v-btn>
              <v-btn small value="accepted">Zugesagt</v-btn>
              <v-btn small value="rejected">Abgesagt</v-btn>
            </v-btn-toggle>
          </div>

          <!-- Forward-Historie -->
          <div v-if="currentApplication.forwardHistory && currentApplication.forwardHistory.length > 0">
            <v-divider class="my-4"></v-divider>
            <h4 class="mb-2">
              <v-icon left small>mdi-share-variant</v-icon>
              Weiterleitungen ({{ currentApplication.forwardHistory.length }})
            </h4>
            <v-card
              v-for="(fwd, idx) in [...currentApplication.forwardHistory].reverse()"
              :key="idx"
              flat
              color="rgba(255,165,0,0.1)"
              class="pa-3 mb-2">
              <div class="d-flex justify-space-between align-center mb-1">
                <strong>
                  {{ fwd.forwardedBy ? fwd.forwardedBy.vorname + " " + fwd.forwardedBy.nachname : "?" }}
                </strong>
                <span class="caption grey--text">{{ formatDateTime(fwd.forwardedAt) }}</span>
              </div>
              <div class="caption">
                <span class="grey--text">An:</span>
                <span v-for="u in fwd.to" :key="u._id" class="ml-1">
                  {{ u.vorname }} {{ u.nachname }}{{ fwd.to.indexOf(u) < fwd.to.length - 1 ? "," : "" }}
                </span>
              </div>
              <div v-if="fwd.reason" class="mt-2" style="white-space: pre-wrap; font-size: 13px">
                <em>"{{ fwd.reason }}"</em>
              </div>
            </v-card>
          </div>

          <!-- Edit-Historie -->
          <div v-if="currentApplication.editHistory && currentApplication.editHistory.length > 0">
            <v-divider class="my-4"></v-divider>
            <h4 class="mb-2">
              <v-icon left small>mdi-history</v-icon>
              Bearbeitungshistorie ({{ currentApplication.editHistory.length }})
            </h4>
            <v-expansion-panels dark flat>
              <v-expansion-panel
                v-for="(h, idx) in [...currentApplication.editHistory].reverse()"
                :key="idx"
                style="background: rgba(255, 255, 255, 0.03)">
                <v-expansion-panel-header>
                  <div>
                    <strong>{{ h.user ? h.user.vorname + " " + h.user.nachname : "Unbekannt" }}</strong>
                    <span class="caption grey--text ml-2">{{ formatDateTime(h.editedAt) }}</span>
                    <v-chip x-small class="ml-2" :color="actionColor(h.action)" dark>
                      {{ actionLabel(h.action) }}
                    </v-chip>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div v-for="(c, cidx) in h.changes" :key="cidx" class="mb-2" style="font-size: 13px">
                    <strong>{{ c.field }}:</strong>
                    <span style="color: #c0392b">{{ formatHistoryValue(c.oldValue) }}</span>
                    →
                    <span style="color: #27ae60">{{ formatHistoryValue(c.newValue) }}</span>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>

          <v-divider class="my-4"></v-divider>

          <!-- Kommentare -->
          <h4 class="mb-2">
            <v-icon left small>mdi-comment-multiple</v-icon>
            Kommentare ({{ currentApplication.comments ? currentApplication.comments.length : 0 }})
          </h4>
          <div v-if="currentApplication.comments && currentApplication.comments.length > 0">
            <v-card v-for="(c, idx) in currentApplication.comments" :key="idx" class="mb-2 pa-3" color="rgba(255,255,255,0.05)" flat>
              <div class="d-flex justify-space-between align-center mb-1">
                <strong>{{ c.user ? c.user.vorname + " " + c.user.nachname : "Unbekannt" }}</strong>
                <span class="caption grey--text">{{ formatDateTime(c.createdAt) }}</span>
              </div>
              <div style="white-space: pre-wrap">{{ c.comment }}</div>
            </v-card>
          </div>
          <p v-else class="grey--text">Noch keine Kommentare.</p>

          <v-textarea v-model="newComment" label="Kommentar hinzufügen" outlined dense dark rows="2" class="mt-3" hide-details />
          <div class="text-right mt-2">
            <v-btn small color="primary" :disabled="!newComment.trim()" :loading="savingComment" @click="addComment">
              <v-icon left small>mdi-send</v-icon>Kommentar senden
            </v-btn>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn text color="error" v-if="canManage" @click="deleteApplication"> <v-icon left>mdi-delete</v-icon>Löschen </v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="detailDialog = false">Schliessen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- EDIT DIALOG (kommt in Teil 2b) -->
    <job-application-edit-dialog
      v-if="editDialog"
      :value="editDialog"
      :application="currentApplication"
      @input="editDialog = $event"
      @saved="handleSaved" />

    <!-- FORWARD DIALOG (kommt in Teil 2b) -->
    <job-application-forward-dialog
      v-if="forwardDialog"
      :value="forwardDialog"
      :application="currentApplication"
      @input="forwardDialog = $event"
      @forwarded="handleForwarded" />
  </div>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import socketService from "@/socketService"
import { mapState } from "vuex"

import AttachmentList from "./components/AttachmentList.vue"
import JobApplicationEditDialog from "./components/JobApplicationEditDialog.vue"
import JobApplicationForwardDialog from "./components/JobApplicationForwardDialog.vue"

export default {
  name: "JobApplications",
  components: {
    AttachmentList,
    JobApplicationEditDialog,
    JobApplicationForwardDialog,
  },
  data() {
    return {
      userService,
      loading: false,
      savingComment: false,
      applications: [],
      currentApplication: null,
      detailDialog: false,
      editDialog: false,
      forwardDialog: false,
      newComment: "",
      filterStudio: "all",
      filterStatus: "all",
      searchQuery: "",
      tableHeaders: [
        { text: "Eingegangen", value: "createdAt" },
        { text: "Zugewiesen", value: "assignedUsers", sortable: false },
        { text: "Bewerber", value: "applicant", sortable: false },
        { text: "Stelle", value: "jobOffer", sortable: false },
        { text: "Bew. Studio", value: "studio", sortable: false },
        { text: "Anhänge", value: "attachments", sortable: false, align: "center" },
        { text: "Status", value: "status" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
      statusFilterOptions: [
        { text: "Alle", value: "all" },
        { text: "Neu", value: "new" },
        { text: "In Bearbeitung", value: "in_progress" },
        { text: "Zugesagt", value: "accepted" },
        { text: "Abgesagt", value: "rejected" },
      ],
      // Bound Socket-Listener-Refs (damit off() korrekt funktioniert)
      socketHandlers: {},
    }
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    canManage() {
      return this.userService.hasRight("d_manageJobApplications")
    },
    studioOptions() {
      return this.storeStudios.filter((s) => s.code && s.code !== "ot" && s.code !== "alibey").map((s) => ({ _id: s._id, name: s.name }))
    },
    studioFilterOptions() {
      return [{ _id: "all", name: "Alle Studios" }, ...this.studioOptions]
    },
    newCount() {
      return this.applications.filter((a) => a.status === "new").length
    },
    filteredApplications() {
      if (!this.searchQuery || !this.searchQuery.trim()) return this.applications
      const q = this.searchQuery.trim().toLowerCase()
      return this.applications.filter((a) => {
        const haystack = [
          a.firstName,
          a.lastName,
          a.email,
          a.phone,
          a.jobOffer && a.jobOffer.title,
          a.jobOffer && a.jobOffer.type && a.jobOffer.type.title,
          a.studio && a.studio.name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
        return haystack.includes(q)
      })
    },
  },
  methods: {
    async loadApplications() {
      this.loading = true
      try {
        const params = {}
        if (this.canManage && this.filterStudio && this.filterStudio !== "all") params.studio = this.filterStudio
        if (this.filterStatus && this.filterStatus !== "all") params.status = this.filterStatus
        this.applications = await api.jobApplications.fetch(params)
      } catch (e) {
        console.error("Fehler beim Laden:", e)
        this.$toast && this.$toast.error("Bewerbungen konnten nicht geladen werden")
      } finally {
        this.loading = false
      }
    },
    async openDetailDialog(application) {
      try {
        this.currentApplication = await api.jobApplications.fetchOne(application._id)
        this.newComment = ""
        this.detailDialog = true
      } catch (e) {
        console.error("Fehler beim Laden des Details:", e)
      }
    },
    openEditDialog() {
      this.editDialog = true
    },
    openForwardDialog() {
      this.forwardDialog = true
    },
    handleSaved(updated) {
      this.currentApplication = updated
      this.editDialog = false
      this.loadApplications()
    },
    handleForwarded(updated) {
      this.currentApplication = updated
      this.forwardDialog = false
      this.loadApplications()
    },
    async changeStatus(status) {
      if (!this.currentApplication) return
      try {
        const updated = await api.jobApplications.updateStatus(this.currentApplication._id, status)
        this.currentApplication = updated
        this.loadApplications()
      } catch (e) {
        console.error("Fehler beim Statuswechsel:", e)
        this.$toast && this.$toast.error("Status konnte nicht geändert werden")
      }
    },
    async addComment() {
      if (!this.newComment.trim() || !this.currentApplication) return
      this.savingComment = true
      try {
        const updated = await api.jobApplications.addComment(this.currentApplication._id, this.newComment)
        this.currentApplication = updated
        this.newComment = ""
      } catch (e) {
        console.error("Fehler beim Kommentar:", e)
      } finally {
        this.savingComment = false
      }
    },
    async deleteApplication() {
      if (!this.currentApplication) return
      if (!confirm("Bewerbung wirklich löschen? Alle Anhänge werden ebenfalls entfernt.")) return
      try {
        await api.jobApplications.delete(this.currentApplication._id)
        this.detailDialog = false
        this.loadApplications()
        this.$toast && this.$toast.success("Bewerbung gelöscht")
      } catch (e) {
        console.error("Fehler beim Löschen:", e)
      }
    },
    canEdit(application) {
      return application && application.status !== "accepted" && application.status !== "rejected"
    },
    formatDate(d) {
      return d ? this.$moment(d).format("DD.MM.YYYY") : "-"
    },
    formatDateTime(d) {
      return d ? this.$moment(d).format("DD.MM.YYYY HH:mm") : "-"
    },
    formatAddress(addr) {
      if (!addr) return "-"
      const parts = []
      if (addr.street) parts.push(addr.street)
      const cityLine = [addr.zip, addr.city].filter(Boolean).join(" ")
      if (cityLine) parts.push(cityLine)
      return parts.length > 0 ? parts.join(", ") : "-"
    },
    formatHistoryValue(val) {
      if (val === null || val === undefined || val === "") return "-"
      if (Array.isArray(val)) return val.length + " Einträge"
      if (typeof val === "object") {
        if (val.vorname) return `${val.vorname} ${val.nachname || ""}`.trim()
        if (val.name) return val.name
        return JSON.stringify(val)
      }
      return String(val)
    },
    statusColor(s) {
      if (s === "accepted") return "green"
      if (s === "in_progress") return "orange"
      if (s === "rejected") return "red darken-2"
      return "blue"
    },
    statusLabel(s) {
      if (s === "accepted") return "Zugesagt"
      if (s === "in_progress") return "In Bearbeitung"
      if (s === "rejected") return "Abgesagt"
      return "Neu"
    },
    actionColor(a) {
      if (a === "forwarded") return "orange"
      if (a === "status_change") return "blue"
      return "blue-grey"
    },
    actionLabel(a) {
      if (a === "forwarded") return "Weitergeleitet"
      if (a === "status_change") return "Status"
      if (a === "edit") return "Bearbeitet"
      return a || "Aktion"
    },
    // ---------- WebSocket ----------
    setupSocketListeners() {
      this.socketHandlers.onNew = (payload) => {
        // Toast-Hinweis und Liste neu laden
        const name = payload.applicantName || "Neue Bewerbung"
        if (this.$toast) {
          if (payload.forwarded) {
            this.$toast.info(`Bewerbung weitergeleitet: ${name}`)
          } else {
            this.$toast.success(`Neue Bewerbung: ${name}`)
          }
        }
        this.loadApplications()
      }

      this.socketHandlers.onUpdated = (payload) => {
        // Wenn Detail-Dialog gerade offen ist und genau diese Bewerbung betrifft → reload
        if (this.detailDialog && this.currentApplication && this.currentApplication._id === payload.applicationId) {
          this.refreshCurrent()
        }
        this.loadApplications()
      }

      this.socketHandlers.onCommented = (payload) => {
        if (this.detailDialog && this.currentApplication && this.currentApplication._id === payload.applicationId) {
          this.refreshCurrent()
        }
      }

      this.socketHandlers.onRemoved = (payload) => {
        // Falls aktuell offene Bewerbung weitergeleitet wurde → Dialog schliessen
        if (this.detailDialog && this.currentApplication && this.currentApplication._id === payload.applicationId) {
          this.detailDialog = false
          this.$toast && this.$toast.info("Diese Bewerbung wurde an jemand anderen weitergeleitet")
        }
        this.loadApplications()
      }

      socketService.off("jobApplication:new", this.socketHandlers.onNew)
      socketService.on("jobApplication:new", this.socketHandlers.onNew)

      socketService.off("jobApplication:updated", this.socketHandlers.onUpdated)
      socketService.on("jobApplication:updated", this.socketHandlers.onUpdated)

      socketService.off("jobApplication:commented", this.socketHandlers.onCommented)
      socketService.on("jobApplication:commented", this.socketHandlers.onCommented)

      socketService.off("jobApplication:removed", this.socketHandlers.onRemoved)
      socketService.on("jobApplication:removed", this.socketHandlers.onRemoved)
    },
    teardownSocketListeners() {
      socketService.off("jobApplication:new", this.socketHandlers.onNew)
      socketService.off("jobApplication:updated", this.socketHandlers.onUpdated)
      socketService.off("jobApplication:commented", this.socketHandlers.onCommented)
      socketService.off("jobApplication:removed", this.socketHandlers.onRemoved)
    },
    async refreshCurrent() {
      if (!this.currentApplication) return
      try {
        this.currentApplication = await api.jobApplications.fetchOne(this.currentApplication._id)
      } catch (e) {
        console.error("Refresh fehlgeschlagen:", e)
      }
    },
  },
  created() {
    console.log("created")
    this.loadApplications()
    this.setupSocketListeners()
  },
  beforeDestroy() {
    this.teardownSocketListeners()
  },
}
</script>

<style scoped>
.transparent {
  background: transparent !important;
}
</style>
